import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import axios from "axios";
import "./i18n";
import "./App.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingCTA from "./components/FloatingCTA";
import Home from "./pages/Home";
import About from "./pages/About";
import Treatments from "./pages/Treatments";
import Destinations from "./pages/Destinations";
import HowItWorks from "./pages/HowItWorks";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import HairTransplant from "./pages/HairTransplant";
import DentalCare from "./pages/DentalCare";
import CosmeticSurgery from "./pages/CosmeticSurgery";
import Login from "./pages/Login";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import WorkerDashboard from "./pages/WorkerDashboard";

// Setup global Axios interceptor to attach Supabase tokens to the Python backend
// We read synchronously from localStorage to avoid concurrent fetch conflicts with Supabase auth calls
axios.interceptors.request.use((config) => {
  try {
    const storageKey = `sb-ewuhktxlxkvvrmgcwsnk-auth-token`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      const token = parsed?.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // Silently fail — user is just not logged in
  }
  return config;
});

const MARKETING_PATHS = ["/", "/about", "/treatments", "/destinations", "/how-it-works", "/contact", "/faq", "/privacy-policy"];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RTLManager() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  const isMarketing = MARKETING_PATHS.includes(location.pathname);
  return (
    <>
      {isMarketing && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/treatments" element={<Treatments />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/treatments/hair-transplant" element={<HairTransplant />} />
          <Route path="/treatments/dental-care" element={<DentalCare />} />
          <Route path="/treatments/cosmetic-surgery" element={<CosmeticSurgery />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/worker" element={<ProtectedRoute requireWorker><WorkerDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      {isMarketing && <Footer />}
      {isMarketing && <FloatingCTA />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RTLManager />
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
